/**
 * GoogleSignInButton — renders the official Google Identity Services button and
 * hands the returned ID token (`credential`) to `onCredential`.
 *
 * Renders nothing when `GOOGLE_CLIENT_ID` is unset (the login page falls back to
 * its placeholder), so a build without a configured client id ships no Google
 * script at all. The GIS client script is loaded once, lazily.
 */
import { useEffect, useRef, type ReactElement } from 'react';

import { GOOGLE_CLIENT_ID } from '../config.js';

const GSI_SRC = 'https://accounts.google.com/gsi/client';

interface GsiId {
  initialize(config: { client_id: string; callback: (resp: { credential: string }) => void }): void;
  renderButton(parent: HTMLElement, options: Record<string, unknown>): void;
}
interface GsiWindow extends Window {
  google?: { accounts?: { id?: GsiId } };
}

let gsiPromise: Promise<void> | null = null;

/** Load the GIS client script exactly once; resolves when `google.accounts.id` is ready. */
function loadGsi(): Promise<void> {
  if (gsiPromise) return gsiPromise;
  gsiPromise = new Promise<void>((resolve, reject) => {
    if ((window as GsiWindow).google?.accounts?.id) {
      resolve();
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GSI_SRC}"]`);
    const onLoad = (): void => resolve();
    const onError = (): void => reject(new Error('Could not load Google sign-in'));
    if (existing) {
      existing.addEventListener('load', onLoad, { once: true });
      existing.addEventListener('error', onError, { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = GSI_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', onLoad, { once: true });
    script.addEventListener('error', onError, { once: true });
    document.head.appendChild(script);
  });
  return gsiPromise;
}

export interface GoogleSignInButtonProps {
  onCredential: (credential: string) => void;
  onError?: (message: string) => void;
}

export function GoogleSignInButton({
  onCredential,
  onError,
}: GoogleSignInButtonProps): ReactElement | null {
  const containerRef = useRef<HTMLDivElement>(null);
  // Keep the latest callbacks without re-running the (one-shot) GIS init effect.
  const onCredentialRef = useRef(onCredential);
  const onErrorRef = useRef(onError);
  onCredentialRef.current = onCredential;
  onErrorRef.current = onError;

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    let cancelled = false;
    loadGsi()
      .then(() => {
        if (cancelled || !containerRef.current) return;
        const id = (window as GsiWindow).google?.accounts?.id;
        if (!id) return;
        id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (resp) => onCredentialRef.current(resp.credential),
        });
        // Theme the button to the active dashboard theme (set on <html>).
        const dark = document.documentElement.dataset.theme !== 'light';
        id.renderButton(containerRef.current, {
          type: 'standard',
          theme: dark ? 'filled_black' : 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'rectangular',
          logo_alignment: 'center',
          width: 330,
        });
      })
      .catch((err: unknown) => {
        onErrorRef.current?.(err instanceof Error ? err.message : 'Google sign-in unavailable');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!GOOGLE_CLIENT_ID) return null;
  return <div ref={containerRef} className="flex justify-center" data-testid="google-signin" />;
}
